/**
 * What the server's answer to `POST /api/subscription/{id}` means for the member standing in front
 * of the screen.
 *
 * Pure on purpose, like `subscriptionStatus.ts` next door: `PaymentStatus.tsx` reaches the shared
 * axios instance, and this app's Jest (CRA 5 / jest 27) cannot import axios 1.7.x at all — its ESM
 * `exports` map defeats the resolver — so the decision worth testing has to live in a module that
 * imports nothing.
 *
 * The bug this table exists to prevent: since the monthly-only cutover every purchase reaches the
 * server through the SetupIntent flow, and Stripe's default `payment_behavior` does not refuse a
 * card it cannot charge — it creates the subscription `incomplete` and returns a PaymentIntent that
 * was declined or is waiting for a 3-D Secure challenge. The screen discarded that answer entirely
 * and painted every one of those as "Platba prebehla úspešne!" over an account that had been
 * granted nothing and charged nothing. There are four outcomes here and only one of them is a
 * success.
 */

import { isPaidThroughADate } from './subscriptionStatus';

/**
 * - `granted` — the charge is settled, the membership can be mirrored locally and celebrated.
 * - `needs_action` — 3-D Secure: the browser has to finish the challenge before anything is known.
 * - `declined` — the card said no. Nothing is pending; the member needs a different card.
 * - `pending` — Stripe has not finished, or said something this table does not recognise. The
 *   webhook settles it, so the member is told to wait rather than promised or refused anything.
 */
export type Outcome = 'granted' | 'needs_action' | 'declined' | 'pending';

/** The three fields `StripeService.createSubscription` reports the first charge with. */
export interface SubscriptionCreated {
    status?: string;
    paymentIntentStatus?: string | null;
    clientSecret?: string;
}

/** PaymentIntent statuses that mean the card was refused. There is nothing left to wait for. */
export const DECLINED_INTENT_STATUSES = ['requires_payment_method', 'canceled'];

export function outcomeOf(result: SubscriptionCreated): Outcome {
    const { status, paymentIntentStatus, clientSecret } = result;

    // A subscription with no first charge to make — a €0 trial — carries no PaymentIntent at all,
    // so its own status is the whole answer. Where there is an intent, `succeeded` says the same
    // thing a beat before the webhook does, which is the entire reason this screen calls the
    // server rather than waiting.
    //
    // `isPaidThroughADate` rather than a second copy of ['active', 'trialing']: it is the same
    // question the billing screen asks of the same Stripe statuses, it lives one module along, and
    // it imports nothing — so nothing is dragged into this file's tests by borrowing it.
    if ((!!status && isPaidThroughADate(status)) || paymentIntentStatus === 'succeeded') {
        return 'granted';
    }

    // The challenge can only be opened with the secret, so `requires_action` without one is not an
    // action anybody can take. It falls through to `pending` rather than sending the member into a
    // dialog that cannot open — which is how the old code failed, minus the green tick.
    if (paymentIntentStatus === 'requires_action' && !!clientSecret) {
        return 'needs_action';
    }

    if (!!paymentIntentStatus && DECLINED_INTENT_STATUSES.includes(paymentIntentStatus)) {
        return 'declined';
    }

    return 'pending';
}

/** Everything `outcomeOf` can answer except the one outcome that is a question rather than a result. */
export type SettledOutcome = Exclude<Outcome, 'needs_action'>;

/**
 * What `stripe.confirmCardPayment` did, read off the PaymentIntent it hands back.
 *
 * Deliberately the same table as the intent half of `outcomeOf` above, because it is the same
 * PaymentIntent and the same statuses — only read a moment later, after the bank's dialog closed.
 * The screen used to decide this with `status === 'succeeded' ? 'granted' : 'declined'`, which
 * contradicted this very module: a `processing` charge (Stripe has the money and is still settling
 * it) and a network failure after the challenge (an error, and no intent at all) were both reported
 * to the member as "Platba bola zamietnutá. Skúste inú kartu." — about a charge that had succeeded
 * or was about to. Being told to try another card is what sends them back to buy a second time.
 *
 * Pass `paymentIntent?.status`, falling back to the intent Stripe.js hangs off its own error object;
 * `null` or `undefined` when there is neither, which lands on `pending` like anything else this
 * table does not recognise. The webhook settles all of them.
 */
export function confirmationOutcome(intentStatus: string | null | undefined): SettledOutcome {
    if (intentStatus === 'succeeded') {
        return 'granted';
    }
    if (!!intentStatus && DECLINED_INTENT_STATUSES.includes(intentStatus)) {
        return 'declined';
    }
    return 'pending';
}

/**
 * What the local mirror — `PUT /api/account/premium/{id}` — managed to do, read from its status
 * code alone, for a member whose charge Stripe has ALREADY confirmed. That is the only place it is
 * called from, and it is what makes two of these codes successes rather than delays.
 *
 * That endpoint exists so a member who has just paid does not sit looking at a free account until
 * the webhook lands; it is a convenience over the top of Stripe's own record, never the authority.
 * 402 — "I cannot match this payment in Stripe yet" — is routine rather than exceptional for a
 * subscription: `findPaidEntitlementDays` only lists ACTIVE subscriptions, so there is a real window
 * between the charge settling and Stripe moving the subscription on. It is not news about the money:
 * it moved, and the webhook grants the membership. Reporting it as "wait" left a paid-up member
 * reading an instruction to refresh the purchase page.
 *
 * 409 is here as a mapping, not as a case: `UserController.updatePremiumUser` answers 200, 402, 503
 * or 404 and nothing else, and does not emit a billing-link conflict. Other endpoints can answer 409 for registration
 * or ambiguous customer ownership. It is
 * kept because if this endpoint ever did grow an "already recorded" conflict — the webhook winning
 * the race is the obvious shape for one — that is a member who has paid, and the alternative arm
 * below would show them an error over a charge that went through. Written down as unreachable so
 * nobody reads it as a description of what the server sends.
 *
 * 503 is the one that genuinely leaves something open — Stripe was unreachable from the server, so
 * nothing was verified and there is no updated account to show. Anything else (a 500, a 400, a
 * network error with no status at all) is a real failure and says so.
 */
export function mirrorOutcome(httpStatus: number | null): 'granted' | 'pending' | 'error' {
    if (httpStatus !== null && httpStatus >= 200 && httpStatus < 300) {
        return 'granted';
    }
    if (httpStatus === 402 || httpStatus === 409) {
        return 'granted';
    }
    if (httpStatus === 503) {
        return 'pending';
    }
    return 'error';
}
